// Levi-primary authenticated-dashboard hero · V-P2 modernization (Feb 2026).
//
// Product Owner-authorized dashboard TOP hero:
//   • Approved worksite Levi scene replaces the previous small circular
//     portrait. Asset: /brand/dashboard/hero_levi_worksite_v1.png
//   • Personalized time-of-day + preferred name greeting (dynamic).
//   • Headline: "What are we getting done today?"
//   • Subtitle: "Start with a job, a customer, or whatever needs your
//     attention."
//   • "Put Levi to Work" (red) — unchanged behavior: primeTts + openDrawer.
//   • "Show Levi the Job" — semantically equivalent to conversational
//     "Activate Vision". Uses the canonical `/api/levi/command` endpoint
//     via `submitLeviCommandProgrammatic` from `useLeviCommand.js`, so
//     the server owns the context-aware routing decision (active project
//     → project-scoped Vision; no project → session-scoped Vision).
//     Zero duplicated routing logic on the Dashboard side.
//   • "Give Levi Job Info" REMOVED from the Dashboard per PO decision.
//     Project-specific intake belongs inside ProjectDetail via the
//     existing `GiveLeviProjectInformation` surface.
//   • Four quick actions preserved (Start an estimate, Show what needs
//     attention, Find a project, Find a document) — same behavior.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, Camera, FileText, Bell, FolderKanban, FileSearch,
} from "lucide-react";
import { useLeviDrawer } from "@/components/LeviLauncher";
import { submitLeviCommandProgrammatic } from "@/hooks/useLeviCommand";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { primeTts } from "@/lib/leviSpeech";
import { leviPreferredName } from "@/lib/leviName";

export default function LeviPrimaryHero() {
  const { openDrawer } = useLeviDrawer();
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const es = lang === "es";
  const [visionSubmitting, setVisionSubmitting] = useState(false);

  const firstName = useMemo(() => leviPreferredName(user), [user]);
  const timeOfDay = useMemo(() => {
    const hour = new Date().getHours();
    if (es) return hour < 12 ? "Buenos días" : hour < 18 ? "Buenas tardes" : "Buenas noches";
    return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  }, [es]);

  const overline  = "LEVI";
  const greeting  = firstName ? `${timeOfDay}, ${firstName}.` : `${timeOfDay}.`;
  const question  = es ? "¿Qué vamos a lograr hoy?" : "What are we getting done today?";
  const subtitle  = es
    ? "Empieza con un trabajo, un cliente o lo que necesite tu atención."
    : "Start with a job, a customer, or whatever needs your attention.";
  const putToWork = es ? "Poner a Levi a trabajar" : "Put Levi to Work";
  const showJob   = es ? "Muéstrale el trabajo a Levi" : "Show Levi the Job";
  const heroAlt   = es
    ? "Levi, con su gorra JobClose AI, revisando planos con el contratista en el sitio de trabajo."
    : "Levi, wearing his approved JobClose AI cap, reviewing plans with the contractor at the worksite.";

  const quickActions = es
    ? [
        { key: "start-estimate", icon: FileText,     label: "Iniciar un estimado" },
        { key: "show-attention", icon: Bell,         label: "Mostrar lo que necesita atención" },
        { key: "find-project",   icon: FolderKanban, label: "Buscar un proyecto" },
        { key: "find-document",  icon: FileSearch,   label: "Buscar un documento" },
      ]
    : [
        { key: "start-estimate", icon: FileText,     label: "Start an estimate" },
        { key: "show-attention", icon: Bell,         label: "Show what needs attention" },
        { key: "find-project",   icon: FolderKanban, label: "Find a project" },
        { key: "find-document",  icon: FileSearch,   label: "Find a document" },
      ];

  const openLevi = (source) => {
    primeTts(`levi-hero-${source}`);
    openDrawer();
  };

  // Dashboard entry for canonical Vision. Fires the SAME text intent
  // ("activate vision") through the SAME `/api/levi/command` endpoint
  // conversational activation uses. The server resolves active project
  // context and returns `action_type: "open_camera"` with the routing
  // surface + IDs; the helper honors that response and navigates
  // accordingly. Zero client-side routing logic.
  const activateVision = async () => {
    if (visionSubmitting) return;
    setVisionSubmitting(true);
    primeTts("levi-hero-show-job");
    try {
      const text = es ? "activar vision" : "activate vision";
      await submitLeviCommandProgrammatic({ text, language: lang, navigate });
    } catch (err) {
      const msg = es
        ? "No se pudo activar Vision. Inténtalo de nuevo."
        : "Could not activate Vision. Please try again.";
      toast.error(msg);
    } finally {
      setVisionSubmitting(false);
    }
  };

  return (
    <section
      data-testid="levi-primary-hero"
      aria-labelledby="levi-primary-hero-title"
      className="relative overflow-hidden rounded-2xl ring-1 ring-black/5 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.25)] mb-6 bg-white"
    >
      {/* Desktop: approved worksite scene spans the full hero width;
          overlay lands on the intentional clean area on the right. */}
      <img
        src="/brand/dashboard/hero_v2.png"
        alt={heroAlt}
        data-testid="levi-primary-hero-img"
        className="hidden lg:block w-full h-auto select-none"
        draggable={false}
      />
      {/* Mobile: stacked artwork above the copy so Levi is never covered. */}
      <img
        src="/brand/dashboard/hero_v2.png"
        alt={heroAlt}
        className="lg:hidden w-full h-auto select-none"
        draggable={false}
      />

      {/* Desktop overlay */}
      <div className="hidden lg:flex absolute inset-0 items-start justify-end pointer-events-none pt-8 xl:pt-10">
        <div className="w-[46%] max-w-[560px] mr-[3%] pointer-events-auto">
          <div className="text-xs font-extrabold tracking-[0.2em] uppercase text-[var(--jc-blue)] mb-2">
            {overline}
          </div>
          <div className="text-lg font-semibold text-[var(--jc-text)] mb-1" data-testid="levi-primary-hero-greeting">
            {greeting}
          </div>
          <h1
            id="levi-primary-hero-title"
            data-testid="levi-primary-hero-title"
            className="font-display text-3xl xl:text-4xl leading-tight text-[var(--jc-text)]"
          >
            {question}
          </h1>
          <p className="mt-3 text-lg font-medium text-[var(--jc-text)] leading-relaxed">
            {subtitle}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => openLevi("put-to-work")}
              data-testid="levi-hero-put-to-work-btn"
              className="inline-flex items-center gap-2 rounded-md bg-red-600 text-white px-5 py-3 text-sm font-semibold hover:bg-red-700 shadow-lg"
            >
              <Zap size={15} /> {putToWork}
            </button>
            <button
              type="button"
              onClick={activateVision}
              disabled={visionSubmitting}
              data-testid="levi-hero-show-job-btn"
              className="inline-flex items-center gap-2 rounded-md bg-white border border-[var(--jc-blue)] text-[var(--jc-blue)] px-5 py-3 text-sm font-semibold hover:bg-[var(--jc-blue)]/5 shadow-sm disabled:opacity-60"
            >
              <Camera size={15} /> {showJob}
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="levi-hero-quick-actions">
            {quickActions.map((qa) => (
              <button
                key={qa.key}
                type="button"
                onClick={() => openLevi(qa.key)}
                data-testid={`levi-hero-quick-${qa.key}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--jc-border-strong)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--jc-text)] hover:bg-[var(--jc-bg)]"
              >
                <qa.icon size={12} /> {qa.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile stacked copy */}
      <div className="lg:hidden px-5 py-6 bg-white" data-testid="levi-hero-mobile-copy">
        <div className="text-xs font-extrabold tracking-[0.2em] uppercase text-[var(--jc-blue)] mb-1">{overline}</div>
        <div className="text-lg font-semibold text-[var(--jc-text)] mb-1">{greeting}</div>
        <h1 className="font-display text-2xl sm:text-3xl leading-tight text-[var(--jc-text)]">{question}</h1>
        <p className="mt-2 text-lg font-medium text-[var(--jc-text)] leading-relaxed">{subtitle}</p>
        <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => openLevi("put-to-work")}
            data-testid="levi-hero-put-to-work-btn-mobile"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-red-600 text-white px-5 py-3 text-sm font-semibold hover:bg-red-700 shadow-lg"
          >
            <Zap size={15} /> {putToWork}
          </button>
          <button
            type="button"
            onClick={activateVision}
            disabled={visionSubmitting}
            data-testid="levi-hero-show-job-btn-mobile"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-white border border-[var(--jc-blue)] text-[var(--jc-blue)] px-5 py-3 text-sm font-semibold hover:bg-[var(--jc-blue)]/5 disabled:opacity-60"
          >
            <Camera size={15} /> {showJob}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {quickActions.map((qa) => (
            <button
              key={qa.key}
              type="button"
              onClick={() => openLevi(qa.key)}
              data-testid={`levi-hero-quick-${qa.key}-mobile`}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--jc-border-strong)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--jc-text)] hover:bg-[var(--jc-bg)]"
            >
              <qa.icon size={12} /> {qa.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
